// Analytics tracking — typed helpers, UTM first-touch capture, sendBeacon
// for unload events, and an in-memory retry queue.
//
// Why this file is large: pre-rewrite we had ~3 generic call sites (pageview,
// search, cart_add, inquiry_submit) and a single untyped `trackEvent`. That
// left every interesting B2B-revenue event invisible — checkout funnel, doc
// downloads, product views per CAS, outbound clicks, search-result clicks,
// 404s. The named helpers below force every call site to pass the right
// dimensions in the right shape so the admin dashboard can aggregate
// without guessing JSONB key conventions.
//
// Architecture:
//   trackEvent(type, opts) — base. Inserts into analytics_events.
//   ┌─ retry queue: failed inserts (network blip, RLS, anon throttle) are
//   │  re-pushed up to 3× over ~6s. Survives page navigation because the
//   │  queue is on `globalThis`, not a React ref.
//   ├─ sendBeacon path: for cart_abandon + page-exit signals, we need to
//   │  POST in the unload handler. `fetch` is unreliable on unload; the
//   │  Beacon API is the only browser-supported way to ship a small JSON
//   │  blob from `unload`/`pagehide`. Falls through to a sync fetch when
//   │  the beacon API is missing (older Safari).
//   └─ UTM first-touch: on first event of a session we capture utm_*,
//      gclid, fbclid, msclkid, and stash them in sessionStorage. Every
//      subsequent event in the session carries them as props.attribution
//      so the funnel (search → cart → checkout) is attributable to the
//      original campaign.

import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { logger } from "@/utils/logger";

const SESSION_KEY = "analytics-session-id";
const ATTRIBUTION_KEY = "analytics-first-touch";
const PAGEVIEW_KEY = "analytics-pageview-state";

/* ---------------------------------------------------------------- session */

const getSessionId = (): string => {
  if (typeof window === "undefined") return "ssr";
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = crypto.randomUUID();
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return crypto.randomUUID();
  }
};

/* ---------------------------------------------------- first-touch attribution */

interface FirstTouch {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  landing_path?: string;
  landing_referrer?: string;
  captured_at?: string;
}

const ATTR_KEYS = ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content", "gclid", "fbclid", "msclkid"] as const;

const getFirstTouch = (): FirstTouch => {
  if (typeof window === "undefined") return {};
  try {
    const cached = sessionStorage.getItem(ATTRIBUTION_KEY);
    if (cached) return JSON.parse(cached) as FirstTouch;

    // First event of this session — extract attribution from URL + referrer
    // and normalise (lowercase + trim) so "LinkedIn" and "linkedin" don't
    // split into separate campaigns in the dashboard.
    const url = new URL(window.location.href);
    const ft: FirstTouch = {
      landing_path: url.pathname,
      landing_referrer: document.referrer || undefined,
      captured_at: new Date().toISOString(),
    };
    let hasAny = false;
    for (const k of ATTR_KEYS) {
      const v = url.searchParams.get(k);
      if (v) {
        ft[k] = v.toLowerCase().trim();
        hasAny = true;
      }
    }
    // Even when the URL has no UTM params, persist landing path + referrer
    // so we can attribute organic referral traffic too.
    if (hasAny || ft.landing_referrer || ft.landing_path) {
      sessionStorage.setItem(ATTRIBUTION_KEY, JSON.stringify(ft));
    }
    return ft;
  } catch {
    return {};
  }
};

/* ----------------------------------------------------------- client snapshot */

const CLIENT_CONTEXT: Record<string, unknown> = (() => {
  if (typeof window === "undefined") return {};
  try {
    return {
      ua: navigator.userAgent || null,
      lang: navigator.language || null,
      tz: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
      // tzOffsetMinutes lets the dashboard convert each event's created_at
      // (UTC) to the *visitor's* local hour for hour-of-day heatmaps,
      // instead of skewing to the admin's local time as it did pre-rewrite.
      tzOffsetMinutes: new Date().getTimezoneOffset(),
      vw: window.innerWidth || null,
      vh: window.innerHeight || null,
      sw: window.screen?.width || null,
      sh: window.screen?.height || null,
      dpr: window.devicePixelRatio || null,
    };
  } catch {
    return {};
  }
})();

/* ----------------------------------------------------------------- retry queue */

interface QueuedEvent {
  attempts: number;
  payload: {
    event_type: string;
    path: string | null;
    props: Record<string, unknown>;
    session_id: string;
    referrer: string | null;
    user_id: string | null;
  };
}

// Use globalThis so navigation between SPA routes doesn't lose the queue.
const QUEUE_KEY = "__longshine_analytics_queue__";
type GlobalWithQueue = typeof globalThis & { [QUEUE_KEY]?: QueuedEvent[] };
const queue = (): QueuedEvent[] => {
  const g = globalThis as GlobalWithQueue;
  if (!g[QUEUE_KEY]) g[QUEUE_KEY] = [];
  return g[QUEUE_KEY]!;
};

const flushQueue = async () => {
  const q = queue();
  if (q.length === 0) return;
  const pending = q.splice(0, q.length);
  for (const item of pending) {
    try {
      const { error } = await supabase.from("analytics_events").insert(item.payload);
      if (error) throw error;
    } catch (err) {
      item.attempts += 1;
      if (item.attempts < 3) {
        q.push(item);
      } else {
        /* Analytics drops are best-effort telemetry, not real product
           errors — network blips, ad-blockers, and dev-without-Supabase
           all trigger them. logger.error was flooding devtools with
           red text and pushing real errors out of view. Downgrade to
           debug so the drop is inspectable when needed but doesn't
           corrupt the visible error signal in the console. */
        logger.debug("Analytics: dropped event after 3 retries", { type: item.payload.event_type, err });
      }
    }
  }
  if (q.length > 0) {
    // exponential-ish: 2s, 4s, 6s
    setTimeout(flushQueue, 2000 * Math.min(q[0].attempts, 3));
  }
};

/* ----------------------------------------------------------------- base event */

interface TrackOptions {
  path?: string;
  props?: Record<string, unknown>;
  /** Use sendBeacon — required for events fired from unload/pagehide. */
  beacon?: boolean;
}

const buildPayload = async (eventType: string, opts: TrackOptions) => {
  let userId: string | null = null;
  try {
    const { data } = await supabase.auth.getUser();
    userId = data.user?.id ?? null;
  } catch {
    /* anon session — no user attached */
  }
  return {
    event_type: eventType,
    path: opts.path ?? (typeof window !== "undefined" ? window.location.pathname : null),
    props: {
      ...(opts.props ?? {}),
      client: CLIENT_CONTEXT,
      attribution: getFirstTouch(),
    },
    session_id: getSessionId(),
    referrer: typeof document !== "undefined" ? document.referrer || null : null,
    user_id: userId,
  };
};

/**
 * Fire a single analytics event. Failures retry up to 3× with backoff.
 * Pass `beacon: true` for events fired during page unload (cart abandon,
 * exit events) — `fetch` won't deliver during unload, but `sendBeacon`
 * does. Network failures during unload are unrecoverable; that's fine.
 */
export async function trackEvent(eventType: string, opts: TrackOptions = {}): Promise<void> {
  try {
    const payload = await buildPayload(eventType, opts);

    if (opts.beacon && typeof navigator !== "undefined" && navigator.sendBeacon) {
      // Use Supabase's PostgREST endpoint directly via the Beacon API.
      // We can't reuse the supabase-js client here because it uses fetch
      // internally, which the browser will abort on unload.
      const url = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/analytics_events`;
      const apikey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY as string;
      const blob = new Blob([JSON.stringify(payload)], { type: "application/json" });
      // sendBeacon doesn't accept custom headers, but PostgREST also accepts
      // apikey + Authorization as URL params. Construct the URL with the
      // apikey query param so the row insert succeeds RLS-wise.
      const beaconUrl = `${url}?apikey=${encodeURIComponent(apikey)}`;
      navigator.sendBeacon(beaconUrl, blob);
      return;
    }

    const { error } = await supabase.from("analytics_events").insert(payload);
    if (error) {
      // Queue for retry rather than dropping.
      queue().push({ attempts: 1, payload });
      setTimeout(flushQueue, 2000);
    }
  } catch (err) {
    logger.error("Analytics tracking failed:", err);
  }
}

/* ----------------------------------------------------------------- typed helpers */
// One helper per event type. The point of having helpers (rather than letting
// every caller invent its own event name + props shape) is that the admin
// dashboard can rely on a fixed schema — `event_type === 'product_view'`
// guarantees props.cas_number is a string, etc. — without defensive coding.

export const trackPageView = (path?: string) =>
  trackEvent("pageview", { path });

export const trackProductView = (cas_number: string, name: string, category?: string | null) =>
  trackEvent("product_view", { props: { cas_number, name, category: category ?? null } });

export const trackSearchQuery = (query: string, result_count: number, surface = "catalog") =>
  trackEvent("search", { props: { query: query.trim().toLowerCase(), result_count, surface, zero_result: result_count === 0 } });

export const trackSearchClick = (query: string, cas_number: string, rank: number) =>
  trackEvent("search_click", { props: { query: query.trim().toLowerCase(), cas_number, rank } });

export const trackAddToCart = (
  cas_number: string,
  name: string,
  category: string | null | undefined,
  quantity: number,
  unit_price?: number,
) =>
  trackEvent("cart_add", { props: { cas_number, name, category: category ?? null, quantity, unit_price: unit_price ?? null } });

export const trackCheckoutStart = (item_count: number, cart_value: number | null) =>
  trackEvent("checkout_start", { props: { item_count, cart_value } });

export const trackCheckoutComplete = (order_id: string, item_count: number, total_value: number | null, currency = "USD") =>
  trackEvent("checkout_complete", { props: { order_id, item_count, total_value, currency } });

export const trackCartAbandon = (item_count: number, cart_value: number | null) =>
  trackEvent("cart_abandon", { props: { item_count, cart_value }, beacon: true });

export const trackInquirySubmit = (
  inquiry_type: string,
  cart_item_count: number,
  country?: string | null,
) =>
  trackEvent("inquiry_submit", { props: { inquiry_type, cart_item_count, country: country ?? null } });

export const trackDocumentDownload = (doc_type: "coa" | "sds" | "spec" | "brochure", cas_number?: string | null, label?: string) =>
  trackEvent("document_download", { props: { doc_type, cas_number: cas_number ?? null, label: label ?? null } });

export const trackOutboundClick = (url: string, label?: string | null) =>
  trackEvent("outbound_click", { props: { url, host: safeHost(url), label: label ?? null } });

export const trackScrollDepth = (path: string, depth_pct: 25 | 50 | 75 | 100) =>
  trackEvent("scroll_depth", { path, props: { depth_pct } });

export const trackNotFound = (path: string) =>
  trackEvent("not_found", { path, props: { from_referrer: typeof document !== "undefined" ? document.referrer || null : null } });

const safeHost = (url: string): string | null => {
  try {
    return new URL(url).host;
  } catch {
    return null;
  }
};

/* ----------------------------------------------------------------- hooks */

/**
 * Auto-fire a pageview on every router pathname change. Mount once at the
 * app root. Also primes the first-touch attribution cache so even sessions
 * with no other events have UTM data associated.
 */
export function usePageviewTracking() {
  const location = useLocation();

  useEffect(() => {
    if (location.pathname.startsWith("/admin")) return;
    getFirstTouch(); // prime attribution
    trackEvent("pageview", { path: location.pathname + location.search });
    // Reset scroll-depth memo on every navigation so the new page can
    // emit its own 25/50/75/100 milestones.
    try { sessionStorage.removeItem(PAGEVIEW_KEY); } catch { /* ignore */ }
  }, [location.pathname, location.search]);
}

/**
 * Mount once at the app root. Adds a single delegated click listener that
 * fires `outbound_click` whenever the user clicks an <a> with an absolute
 * href pointing at a different host. Avoids individual call sites having
 * to remember to call trackOutboundClick on every social/share link.
 */
export function useOutboundClickTracking() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const handler = (e: MouseEvent) => {
      const a = (e.target as HTMLElement | null)?.closest?.("a[href]") as HTMLAnchorElement | null;
      if (!a) return;
      const href = a.href;
      if (!href || href.startsWith("mailto:") || href.startsWith("tel:") || href.startsWith("#")) return;
      try {
        const url = new URL(href, window.location.href);
        if (url.host === window.location.host) return;
        // fire-and-forget; we can't prevent navigation
        trackOutboundClick(url.href, a.textContent?.trim().slice(0, 80) || null);
      } catch {
        /* relative or malformed — ignore */
      }
    };
    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, []);
}

/**
 * Mount once. Watches the window scroll position on the current route and
 * fires `scroll_depth` at 25/50/75/100% — once each per page. Memoised in
 * sessionStorage so a back/forward navigation doesn't double-count.
 */
export function useScrollDepthTracking() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window === "undefined") return;
    if (location.pathname.startsWith("/admin")) return;
    const fired = new Set<number>();
    const onScroll = () => {
      const doc = document.documentElement;
      const max = doc.scrollHeight - window.innerHeight;
      if (max <= 0) return;
      const pct = Math.round((window.scrollY / max) * 100);
      for (const m of [25, 50, 75, 100] as const) {
        if (pct >= m && !fired.has(m)) {
          fired.add(m);
          trackScrollDepth(location.pathname, m);
        }
      }
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);
}
